/**
 * SP-API Sync V2 — Orders sheet (Path B).
 * SoT: spapi_v2_orders (payment_method, ship_service_level). Not MarketSync v1.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  amazonConnectApi,
  type SellerConnection,
  type SpapiV2Order,
} from '../../api/amazonconnect';
import {
  PAGE_SIZE_OPTIONS,
  PlatformDataGrid,
  type DataGridColumn,
  type DataGridMeta,
  type PageSizeOption,
} from '../../components/platform/PlatformDataGrid';
import { getMembershipId, getWorkspaceId } from '../../lib/auth-storage';
import { WorkspaceHeader } from '../../shell';

const FIXTURE = '171-1794559-8049152';

export function SpapiV2OrdersPage() {
  const workspaceKey = getWorkspaceId() ?? 'default';
  const userKey = getMembershipId() ?? 'anon';
  const [params] = useSearchParams();
  const [connections, setConnections] = useState<SellerConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(50);
  const [qDraft, setQDraft] = useState('');
  const [items, setItems] = useState<SpapiV2Order[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [identity, setIdentity] = useState<string>('');
  const [latencyMs, setLatencyMs] = useState<number | undefined>();

  useEffect(() => {
    void amazonConnectApi
      .listConnections()
      .then((c) => {
        setConnections(c);
        const urlConn = params.get('connection') || '';
        const pick = urlConn && c.some((x) => x.id === urlConn) ? urlConn : c[0]?.id || '';
        setConnectionId((prev) => prev || pick);
      })
      .catch(() => setConnections([]));
  }, [params]);

  const load = useCallback(async () => {
    if (!connectionId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const [res, id] = await Promise.all([
        amazonConnectApi.spapiV2Orders(connectionId, page, pageSize),
        amazonConnectApi.spapiV2Identity(connectionId).catch(() => null),
      ]);
      setLatencyMs(Math.round(performance.now() - t0));
      setItems(res.items ?? []);
      setTotal(res.total ?? 0);
      if (id) {
        setIdentity(
          id.v2_enabled
            ? `V2 on · SPID ${id.selling_partner_id || '—'}${id.certified ? ' · certified' : ''}`
            : 'V2 not enabled — certify identity first',
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Sync V2 orders');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [connectionId, page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  const triggerSync = async (domain: 'orders' | 'order_items') => {
    if (!connectionId) return;
    setSyncing(true);
    try {
      await amazonConnectApi.spapiV2Sync(connectionId, domain);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const connection = connections.find((c) => c.id === connectionId);
  const pages = pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const meta: DataGridMeta = {
    total_count: total,
    returned_count: items.length,
    page,
    page_size: pageSize,
    pages,
    remaining: Math.max(0, total - (page - 1) * pageSize - items.length),
    has_more: page < pages,
    execution_time_ms: latencyMs,
    source: 'spapi_v2_orders',
    data_source_badge: 'Sync V2 · orders',
    last_sync: connection?.last_sync_at,
  };

  const columns: DataGridColumn<SpapiV2Order>[] = useMemo(
    () => [
      {
        id: 'amazon_order_id',
        header: 'Amazon Order ID',
        width: 170,
        stickyLeft: 0,
        render: (r) => (
          <Link
            className="font-mono text-[11px] text-teal-800 underline-offset-2 hover:underline dark:text-teal-300"
            to={`/amazon/spapi-v2/order-items?connection=${encodeURIComponent(connectionId)}&order_id=${encodeURIComponent(r.amazon_order_id)}`}
          >
            {r.amazon_order_id}
          </Link>
        ),
        exportValue: (r) => r.amazon_order_id,
      },
      {
        id: 'purchase_date',
        header: 'Purchase',
        width: 150,
        render: (r) => (
          <span className="font-mono text-[10px]">{r.purchase_date?.slice(0, 19) ?? '—'}</span>
        ),
        exportValue: (r) => r.purchase_date ?? '',
      },
      {
        id: 'order_status',
        header: 'Status',
        width: 110,
        render: (r) => r.order_status || '—',
        exportValue: (r) => r.order_status ?? '',
      },
      {
        id: 'payment_method',
        header: 'Payment',
        width: 90,
        render: (r) => (
          <span className={r.payment_method === 'COD' ? 'font-semibold text-amber-800 dark:text-amber-300' : ''}>
            {r.payment_method || '—'}
          </span>
        ),
        exportValue: (r) => r.payment_method ?? '',
      },
      {
        id: 'ship_service_level',
        header: 'Ship level',
        width: 110,
        render: (r) => r.ship_service_level || r.shipment_service_level_category || '—',
        exportValue: (r) => r.ship_service_level || r.shipment_service_level_category || '',
      },
      {
        id: 'amount',
        header: 'Amount',
        width: 100,
        render: (r) =>
          r.currency ? `${r.currency} ${Number(r.amount ?? 0).toFixed(2)}` : String(r.amount ?? '—'),
        exportValue: (r) => String(r.amount ?? ''),
      },
      {
        id: 'fulfillment_channel',
        header: 'Fulfillment',
        width: 90,
        render: (r) => r.fulfillment_channel || '—',
        exportValue: (r) => r.fulfillment_channel ?? '',
      },
    ],
    [connectionId],
  );

  return (
    <div className="mx-auto max-w-[100rem] space-y-4">
      <WorkspaceHeader
        title="Sync V2 Orders"
        subtitle="Greenfield SP-API sheet — COD + Expedited from getOrders. MarketSync v1 remains paused."
        breadcrumb={[
          { label: 'Founder OS', path: '/command' },
          { label: 'Commerce' },
          { label: 'Sync V2 Orders' },
        ]}
        primaryActions={
          <>
            <button
              type="button"
              disabled={!connectionId || syncing}
              onClick={() => void triggerSync('orders')}
              className="rounded-md bg-[var(--shell-fg)] px-3 py-2 text-sm font-medium text-[var(--shell-canvas)] disabled:opacity-50"
            >
              {syncing ? 'Syncing…' : 'Pull orders'}
            </button>
            <button
              type="button"
              disabled={!connectionId || syncing}
              onClick={() => void triggerSync('order_items')}
              className="rounded-md border border-[var(--shell-border)] px-3 py-2 text-sm font-medium disabled:opacity-50"
            >
              Pull items
            </button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--shell-fg-muted)]">
        <span className="rounded-md bg-[var(--shell-accent-muted)] px-2 py-1 text-[var(--shell-accent)]">
          {identity || '…'}
        </span>
        <Link
          className="underline-offset-2 hover:underline"
          to={`/amazon/spapi-v2/order-items?connection=${encodeURIComponent(connectionId)}`}
        >
          Order items →
        </Link>
        <Link
          className="underline-offset-2 hover:underline"
          to={`/amazon/spapi-v2/order-items?connection=${encodeURIComponent(connectionId)}&order_id=${FIXTURE}`}
        >
          Fixture {FIXTURE}
        </Link>
        <Link className="text-[var(--shell-fg-muted)] underline-offset-2 hover:underline" to="/amazon/orders">
          v1 Orders (paused)
        </Link>
      </div>

      <PlatformDataGrid
        gridId="spapi-v2-orders"
        workspaceKey={workspaceKey}
        userKey={userKey}
        columns={columns}
        rows={items}
        rowKey={(r) => r.amazon_order_id}
        meta={meta}
        loading={loading}
        error={error}
        emptyMessage="No Sync V2 orders yet — pull orders or wait for the 10m tick."
        page={page}
        pageSize={pageSize}
        searchDraft={qDraft}
        onSearchDraftChange={setQDraft}
        onSearch={() => setPage(1)}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          if (PAGE_SIZE_OPTIONS.includes(n)) {
            setPageSize(n);
            setPage(1);
          }
        }}
        onRefresh={() => void load()}
        density="compact"
        toolbarExtra={
          <label className="text-sm">
            <span className="text-xs font-medium text-slate-500">Connection</span>
            <select
              className="mt-1 block min-h-11 min-w-[16rem] rounded-lg border border-slate-300 px-2 dark:border-slate-600 dark:bg-slate-900"
              value={connectionId}
              onChange={(e) => {
                setConnectionId(e.target.value);
                setPage(1);
              }}
            >
              {connections.length === 0 && <option value="">No connections</option>}
              {connections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.seller_ref || c.id.slice(0, 8)} · {c.status}
                </option>
              ))}
            </select>
          </label>
        }
      />
    </div>
  );
}
